/**
 * In-memory generic bbox preload store: coverage, single-flight fetch, no MapLibre coupling.
 */

import {
    bboxContains,
    computePrefetchBboxFromContext,
    shrinkBboxFromCenter,
} from "./bboxPreload.tools";
import type {
    BboxPreloadConfig,
    BboxPreloadOptions,
    FetchForBbox,
    MapBbox,
    MapViewportContext,
    PreloadUpdateResult,
} from "./bboxPreload.types";

/** Maximum prefetch iterations per {@link BboxPreloadStore.updatePreloadIfNeeded} call (safety). */
const MAX_PRELOAD_ITERATIONS_PER_CALL = 4;

/**
 * Options passed to {@link BboxPreloadStore.updatePreloadIfNeeded}.
 */
export interface UpdatePreloadOptions {

    /** Force a fetch even if the current viewport is still covered by the preloaded bbox. */
    "force"?: boolean;
}

/**
 * Holds preloaded DTOs and bbox, decides when to refetch, runs single-flight domain fetches.
 */
export class BboxPreloadStore<TData> {

    private preloadedBbox: MapBbox | null = null;

    private data: TData | null = null;

    private activeLoadPromise: Promise<boolean> | null = null;

    private requestGeneration = 0;

    private pendingViewportContext: MapViewportContext | null = null;

    private fetchAbortController: AbortController | null = null;

    private readonly minPreloadZoom: number;

    private readonly config: BboxPreloadConfig;

    private readonly fetchForBbox: FetchForBbox<TData>;

    private readonly countItems: ( data: TData ) => number;

    private readonly loggerName: string;

    private readonly onFetchError: ( error: unknown, signal: AbortSignal ) => void;

    /**
     * @param minPreloadZoom Zoom threshold below which preload is skipped and data is reset.
     * @param config Preload configuration (multiplier, reference zoom, shrink factor, debounce).
     * @param fetchForBbox Injectable bbox fetch (domain-specific).
     * @param options Optional count/logger/error callbacks.
     */
    constructor(
        minPreloadZoom: number,
        config: BboxPreloadConfig,
        fetchForBbox: FetchForBbox<TData>,
        options?: BboxPreloadOptions<TData>,
    ) {
        this.minPreloadZoom = minPreloadZoom;
        this.config         = config;
        this.fetchForBbox   = fetchForBbox;

        this.countItems = options?.countItems ?? ( ( data: TData ): number => {
            if ( Array.isArray( data ) ) {
                return data.length;
            }

            return 0;
        } );

        this.loggerName = options?.loggerName ?? "BboxPreloadStore";

        this.onFetchError = options?.onFetchError ?? ( ( error: unknown, signal: AbortSignal ): void => {
            if ( signal.aborted ) {
                return;
            }

            console.error( `[${ this.loggerName }] fetch failed`, error );
        } );
    }

    /**
     * Whether the map zoom is high enough to run preload logic.
     *
     * @param currentZoom Current `map.getZoom()`.
     * @returns True when preload/check/fetch are allowed.
     */
    isPreloadZoomEligible( currentZoom: number ): boolean {
        return currentZoom >= this.minPreloadZoom;
    }

    /**
     * Whether a new preload fetch is required for the given viewport.
     *
     * @param context Current viewport snapshot.
     * @param force When true, always returns true to force a refresh.
     * @returns True when no data yet or visible bounds left the inner comfort bbox.
     */
    private shouldUpdatePreload( context: MapViewportContext, force = false ): boolean {
        if ( this.preloadedBbox === null || force ) {
            return true;
        }

        const innerBbox = shrinkBboxFromCenter(
            this.preloadedBbox,
            this.config.prefetchTriggerShrinkFactor,
        );

        return !bboxContains( context.visibleBbox, innerBbox );
    }

    /**
     * Returns the last successfully preloaded data, if any.
     *
     * @returns Domain DTOs or null when the store is empty.
     */
    getPreloadedData(): TData | null {
        if ( this.preloadedBbox === null ) {
            return null;
        }

        return this.data;
    }

    /**
     * Returns the geographic bbox of the last successful preload fetch.
     *
     * @returns Preloaded bbox or null when the store is empty.
     */
    getPreloadedBbox(): MapBbox | null {
        return this.preloadedBbox;
    }

    /**
     * Clears cached bbox and DTOs; aborts an in-flight fetch and bumps generation to drop stale responses.
     */
    reset(): void {
        this.requestGeneration += 1;
        this.fetchAbortController?.abort();
        this.fetchAbortController  = null;
        this.activeLoadPromise     = null;
        this.pendingViewportContext = null;
        this.preloadedBbox         = null;
        this.data                  = null;
    }

    /**
     * Updates preload when needed: waits for any in-flight load, refetches when viewport nears bbox edge.
     *
     * @remarks Implemented as a while-loop (not deep recursion). Concurrent callers await the same flight
     * and re-check with the latest pending viewport context.
     * @param initialContext Viewport snapshot from the map controller.
     * @param options Optional flags (e.g. force a refresh).
     * @returns Status for sticky map display rules.
     */
    async updatePreloadIfNeeded(
        initialContext: MapViewportContext,
        options: UpdatePreloadOptions = {},
    ): Promise<PreloadUpdateResult> {
        if ( !this.isPreloadZoomEligible( initialContext.currentZoom ) ) {
            this.reset();

            return { "status": "belowMinZoom" };
        }

        let context: MapViewportContext          = initialContext;
        let lastResult: PreloadUpdateResult      = { "status": "covered" };
        let iterations                           = 0;
        let force                                = options.force ?? false;

        while ( iterations < MAX_PRELOAD_ITERATIONS_PER_CALL ) {
            iterations += 1;

            const activeLoadWait = this.maybeAwaitActiveLoad( context );
            if ( activeLoadWait !== undefined ) {
                await activeLoadWait;
            }
            context = this.consumePendingViewportContext( context );

            if ( !this.shouldUpdatePreload( context, force ) ) {
                return lastResult.status === "fetched" ? lastResult : { "status": "covered" };
            }

            force = false;

            lastResult = await this.runSingleFetch( context );

            if ( lastResult.status === "covered" && this.preloadedBbox === null ) {
                return lastResult;
            }

            if ( lastResult.status === "failed" ) {
                return lastResult;
            }
        }

        return lastResult;
    }

    /**
     * When a load is active, stores the latest context and returns a promise to await.
     *
     * @param context Viewport context for this caller.
     * @returns A promise to await when a load is in flight, or undefined to continue immediately.
     */
    private maybeAwaitActiveLoad( context: MapViewportContext ): Promise<void> | undefined {
        if ( this.activeLoadPromise === null ) {
            return undefined;
        }

        this.pendingViewportContext = context;

        return this.activeLoadPromise.then( () => {
            /* noop */
        } );
    }

    /**
     * Applies the latest pending viewport after a shared load completes.
     *
     * @param fallback Context to use when no pending override exists.
     * @returns Context to use for the next coverage check.
     */
    private consumePendingViewportContext( fallback: MapViewportContext ): MapViewportContext {
        const pending = this.pendingViewportContext;
        this.pendingViewportContext = null;

        return pending ?? fallback;
    }

    /**
     * Runs one single-flight fetch and updates the store on success.
     *
     * @param context Viewport used to center the prefetch bbox.
     * @returns Fetch outcome for this iteration.
     */
    private async runSingleFetch( context: MapViewportContext ): Promise<PreloadUpdateResult> {
        const prefetchBbox      = computePrefetchBboxFromContext( context, this.config );
        const generation        = this.requestGeneration + 1;
        this.requestGeneration  = generation;

        this.fetchAbortController?.abort();
        const abortController    = new AbortController();
        this.fetchAbortController = abortController;

        const loadTask = this.executeFetch( prefetchBbox, generation, abortController.signal );
        this.activeLoadPromise = loadTask;

        let fetchSucceeded = false;

        try {
            fetchSucceeded = await loadTask;
        } finally {
            if ( this.activeLoadPromise === loadTask ) {
                this.activeLoadPromise = null;
            }

            if ( this.fetchAbortController === abortController ) {
                this.fetchAbortController = null;
            }
        }

        if ( generation !== this.requestGeneration ) {
            return { "status": "covered" };
        }

        if ( !fetchSucceeded ) {
            return { "status": "failed" };
        }

        const itemsFetched = this.data === null ? 0 : this.countItems( this.data );

        if ( import.meta.env.DEV ) {
            console.debug(
                `[${ this.loggerName }]`,
                "fetched",
                {
                    "itemsFetched": itemsFetched,
                    "bbox":         prefetchBbox,
                },
            );
        }

        return {
            "status":       "fetched",
            "fetchedBbox":  prefetchBbox,
            "itemsFetched": itemsFetched,
        };
    }

    /**
     * Performs the HTTP fetch and commits DTOs when the generation is still current.
     *
     * @param prefetchBbox Bbox query for the API.
     * @param generation Request generation captured at start.
     * @param signal Abort signal tied to store reset.
     */
    private async executeFetch(
        prefetchBbox: MapBbox,
        generation: number,
        signal: AbortSignal,
    ): Promise<boolean> {
        try {
            const data = await this.fetchForBbox( prefetchBbox, signal );

            if ( generation !== this.requestGeneration ) {
                return false;
            }

            this.data         = data;
            this.preloadedBbox = prefetchBbox;

            return true;
        } catch( error: unknown ) {
            if ( signal.aborted ) {
                return false;
            }

            this.onFetchError( error, signal );

            return false;
        }
    }

}
